import { RadioGroupOption } from '@types';
import clsx from 'clsx';
import React, { InputHTMLAttributes, Key, useCallback, useEffect, useState, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { Input } from '../Input/Input';

import { RadioElement } from './RadioButton';
import styles from './RadioButton.module.scss';

export interface RadioGroupProps<TKey> {
  options: RadioGroupOption<TKey>[];
  control?: any;
  isTable?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement> & { ref?: React.ForwardedRef<HTMLInputElement> };
  onSelect?: (value: any) => void;
  className?: string;
  rules?: Object;
}

export const RadioGroup = <TKey,>({
  className,
  control,
  options,
  isTable = false,
  inputProps,
  onSelect,
  rules,
}: RadioGroupProps<TKey>) => {
  const [searchTerm, setSearchTerm] = useState('');

  const [value, setValue] = useState(inputProps?.defaultValue?.toString() || inputProps?.value?.toString() || '');

  const filteredOptions = useMemo(
    () =>
      options.filter(
        option =>
          searchTerm === '' ||
          option.name
            .toLowerCase()
            .split(' ')
            .some(optionNameWord => optionNameWord.startsWith(searchTerm.toLowerCase())) ||
          option.name.toLowerCase().startsWith(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  );

  const onChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(event.target?.value);
      inputProps?.onChange?.(event);
    },
    [inputProps],
  );

  useEffect(() => {
    if (inputProps?.defaultValue) {
      setValue(inputProps.defaultValue.toString());
    }
  }, [inputProps?.defaultValue]);

  return control ? (
    <Controller
      name={inputProps.name}
      control={control}
      defaultValue={value}
      rules={rules}
      render={({ fieldState: { error }, field: { value: innerValue, onChange, ref } }) => (
        <div
          className={clsx(styles.group, isTable && styles.isTable, inputProps.readOnly && styles.readOnly, className)}
        >
          {options.length > 6 && (
            <Input
              className={styles.searchInput}
              props={{ placeholder: 'Search list', onChange: e => setSearchTerm(e.target.value) }}
            />
          )}
          <div
            className={styles.list}
            id="radioList"
          >
            {filteredOptions.length === 0 ? (
              <div className={styles.noResults}>No results found</div>
            ) : (
              filteredOptions.map(option => (
                <RadioElement
                  id="radioElement"
                  ref={ref}
                  inputProps={{ ...inputProps, value: option.id as string, defaultValue: undefined }}
                  option={option}
                  key={option.id as Key}
                  isChecked={option.id === innerValue}
                  onChange={e => {
                    const raw = e.target.value;
                    const parsed = Number(raw);
                    const valueToSet = raw !== '' && !Number.isNaN(parsed) && String(parsed) === raw ? parsed : raw;
                    onChange(valueToSet);
                    if (onSelect) {
                      onSelect(valueToSet);
                    }
                  }}
                />
              ))
            )}
          </div>
        </div>
      )}
    />
  ) : (
    <div className={clsx(styles.group, isTable && styles.isTable, className)}>
      {options.length > 6 && (
        <Input
          className={styles.searchInput}
          props={{ placeholder: 'Search list', onChange: e => setSearchTerm(e.target.value) }}
        />
      )}
      <div
        className={styles.list}
        id="radioList"
      >
        {filteredOptions.length === 0 ? (
          <div className={styles.noResults}>No results found</div>
        ) : (
          filteredOptions.map(option => (
            <RadioElement
              id="radioElement"
              inputProps={{ ...inputProps, value: option.id as string, defaultValue: undefined }}
              option={option}
              key={option.id as Key}
              isChecked={option.id.toString() === value?.toString()}
              onChange={e => {
                onChangeHandler(e);
                if (onSelect) {
                  onSelect(option.id);
                }
              }}
            />
          ))
        )}
      </div>
    </div>
  );
};
